import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/auth';

const IncomePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [income, setIncome] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    amount: '',
    currency_id: '',
    description: '',
    create_date: new Date().toISOString().slice(0, 16) // YYYY-MM-DDTHH:MM
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incomeResponse, currenciesResponse] = await Promise.all([
        apiService.income.getAll(),
        apiService.currencies.getAll()
      ]);

      if (incomeResponse.data.status === 'success') {
        setIncome(incomeResponse.data.data);
      }

      if (currenciesResponse.data.status === 'success') {
        setCurrencies(currenciesResponse.data.data);
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        currency_id: parseInt(formData.currency_id),
        user_id: user?.id || 1
      };

      let response;
      if (editingIncome) {
        response = await apiService.income.update({ ...data, id: editingIncome.id });
      } else {
        response = await apiService.income.create(data);
      }

      if (response.data.status === 'success') {
        setSuccess(editingIncome ? 'Income updated successfully!' : 'Income added successfully!');
        handleCloseModal();
        loadData();
      } else {
        setError(response.data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (incomeItem) => {
    setEditingIncome(incomeItem);
    setFormData({
      amount: incomeItem.amount.toString(),
      currency_id: incomeItem.currency_id.toString(),
      description: incomeItem.description || '',
      create_date: new Date(incomeItem.create_date).toISOString().slice(0, 16)
    });
    setShowModal(true);
  };

  const handleDelete = async (incomeId) => {
    if (!window.confirm('Are you sure you want to delete this income entry?')) {
      return;
    }

    try {
      const response = await apiService.income.delete(incomeId);
      if (response.data.status === 'success') {
        setSuccess('Income deleted successfully!');
        loadData();
      } else {
        setError(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIncome(null);
    setFormData({
      amount: '',
      currency_id: '',
      description: '',
      create_date: new Date().toISOString().slice(0, 16)
    });
    setError('');
  };

  const formatAmount = (amount, currency = 'PLN') => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner text="Loading income data..." />;
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="text-white mb-0">
                  <i className="fas fa-plus-circle me-3"></i>
                  Income Management
                </h1>
                <p className="text-light opacity-75">Manage your budget and income sources</p>
              </div>
              <Button 
                className="vienna-btn-primary"
                onClick={() => setShowModal(true)}
              >
                <i className="fas fa-plus me-2"></i>
                Add Income
              </Button>
            </div>
          </Col>
        </Row>

        {error && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {success && (
          <Row className="mb-4">
            <Col>
              <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                {success}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card className="vienna-card">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Income Entries ({income.length})
                </h5>
              </Card.Header>
              <Card.Body>
                {income.length > 0 ? (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Currency</th>
                        <th>Amount in PLN</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {income.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description || 'No description'}</td>
                          <td className="amount-positive fw-bold">
                            {formatAmount(item.amount, item.currency_code)}
                          </td>
                          <td>
                            <span className="currency-badge">{item.currency_code}</span>
                          </td>
                          <td className="amount-positive">
                            {formatAmount(item.amount_in_pln)}
                          </td>
                          <td>{formatDate(item.create_date)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEdit(item)}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <i className="fas fa-plus-circle fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No income entries yet</h5>
                    <p className="text-muted">Start by adding your budget or income sources</p>
                    <Button 
                      className="vienna-btn-primary"
                      onClick={() => setShowModal(true)}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add First Income
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Add/Edit Income Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-plus-circle me-2"></i>
              {editingIncome ? 'Edit Income' : 'Add Income'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Initial budget, Exchange money, etc."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency *</Form.Label>
                    <Form.Select
                      value={formData.currency_id}
                      onChange={(e) => setFormData({...formData, currency_id: e.target.value})}
                      required
                    >
                      <option value="">Select currency</option>
                      {currencies.map((currency) => (
                        <option key={currency.id} value={currency.id}>
                          {currency.code} {currency.is_master_currency && '(Master)'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.create_date}
                  onChange={(e) => setFormData({...formData, create_date: e.target.value})}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" className="vienna-btn-primary">
                <i className="fas fa-save me-2"></i>
                {editingIncome ? 'Update' : 'Add'} Income
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default IncomePage; 